import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Password } from "primereact/password";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { deleteUser, updateUser, exportToSkeyechartSwitch } from "./api";

const UserTable = ({
  filteredData,
  filters,
  header,
  onFilter,
  first,
  rows,
  totalRecords,
  onPageChange,
  openDialog,
  refreshUsers,
  loading,
}) => {
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editedPassword, setEditedPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState(""); // Add this new state
  const [editedRole, setEditedRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [switchLoading, setSwitchLoading] = useState({});

  const toast = React.useRef(null);

  const roles = [
    { label: "Admin", value: "ROLE_ADMIN" },
    { label: "Super User", value: "ROLE_SUPERUSER" },
    { label: "User", value: "ROLE_USER" },
  ];

  const skeyechartAccessTemplate = (rowData) => {
    // Check if the user is an admin
    const isAdmin = rowData.roles.some((role) => role.name === "ROLE_ADMIN");

    return (
      <InputSwitch
        checked={rowData.exportToSkeyechart === true}
        disabled={isAdmin || switchLoading[rowData.id] || false}
        onChange={async (e) => {
          try {
            // Set loading state
            setSwitchLoading((prev) => ({ ...prev, [rowData.id]: true }));

            // Optimistic UI update (immediately show the change)
            const newValue = e.value;

            // Call API
            await exportToSkeyechartSwitch(rowData.id);

            toast.current.show({
              severity: "success",
              summary: "Success",
              detail: `Skeyechart access ${
                newValue ? "enabled" : "disabled"
              } successfully`,
              life: 3000,
            });

            // Refresh data without causing a full re-render
            if (refreshUsers) {
              setTimeout(() => {
                refreshUsers();
              }, 300);
            }
          } catch (error) {
            console.error("Skeyechart access toggle error:", error);
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail: "Failed to update Skeyechart access",
              life: 3000,
            });
          } finally {
            // Reset loading state
            setSwitchLoading((prev) => ({ ...prev, [rowData.id]: false }));
          }
        }}
      />
    );
  };

  const actionBodyTemplate = (rowData) => {
    const isAdmin = rowData.roles.some((role) => role.name === "ROLE_ADMIN");

    if (!isAdmin) {
      return (
        <>
          <Button
            icon="pi pi-plus"
            style={{
              backgroundColor: "#fff",
              color: "#D97A07",
              border: "none",
              fontFamily: "poppins",
              marginRight: "5px",
            }}
            onClick={() => openDialog(rowData)}
          />
          <Button
            icon="pi pi-pencil"
            style={{
              backgroundColor: "#fff",
              color: "#007ad9",
              border: "none",
              fontFamily: "poppins",
              marginRight: "5px",
            }}
            onClick={() => {
              setSelectedUser(rowData);
              setEditedPassword("");
              setEditedRole(rowData.roles[0]?.name);
              setEditDialogVisible(true);
            }}
          />
          <Button
            icon="pi pi-trash"
            style={{
              backgroundColor: "#fff",
              color: "#D90707",
              border: "none",
              fontFamily: "poppins",
            }}
            onClick={() => {
              setSelectedUser(rowData);
              setDeleteDialogVisible(true);
            }}
          />
        </>
      );
    }

    return null;
  };

  const roleBodyTemplate = (rowData) => {
    const getRoleStyle = (roleName) => {
      switch (roleName) {
        case "ROLE_ADMIN":
          return { value: "Admin", backgroundColor: "#D97A07" };
        case "ROLE_SUPERUSER":
          return { value: "Super User", backgroundColor: "#007ad9" };
        case "ROLE_USER":
          return { value: "User", backgroundColor: "#0d2e4d" };
        default:
          return { value: "User", backgroundColor: "#0d2e4d" };
      }
    };

    return rowData.roles.map((role, index) => {
      const { value, backgroundColor } = getRoleStyle(role.name);
      return (
        <Badge
          key={index}
          value={value}
          style={{
            backgroundColor,
            color: "white",
            marginRight: "5px",
            fontFamily: "poppins",
            fontSize: "14px",
          }}
        />
      );
    });
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteUser(selectedUser.id);

      // Only show success if the response status is 200
      if (response && response.status === 200) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "User deleted successfully",
          life: 3000,
        });
        setDeleteDialogVisible(false);

        // Make sure refreshUsers is called
        if (refreshUsers) {
          await refreshUsers();
        }
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete user",
        life: 3000,
      });
    }
  };

  const confirmEdit = async () => {
    try {
      if (editedPassword && editedPassword !== confirmPassword) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Passwords do not match",
          life: 3000,
        });
        return;
      }

      const userData = {
        password: editedPassword || undefined,
        role:
          editedRole && editedRole.includes("ROLE_")
            ? editedRole.replace("ROLE_", "")
            : editedRole,
      };

      const response = await updateUser(selectedUser.id, userData);

      if (response && response.status === 200) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "User updated successfully",
          life: 3000,
        });
        setEditDialogVisible(false);

        // Refresh the table without page reload
        if (refreshUsers) {
          await refreshUsers();
        }
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update user",
        life: 3000,
      });
    }
  };

  const debouncedPageChange = (event) => {
    // Prevent multiple rapid page changes
    if (switchLoading[event?.page]) return;

    // Call the actual page change handler
    onPageChange(event);
  };

  return (
    <div className="table-container">
      <Toast ref={toast} />
      <DataTable
        value={filteredData}
        paginator={false}
        responsiveLayout="scroll"
        filters={filters}
        globalFilterFields={["id", "username", "email", "roles"]}
        header={header}
        emptyMessage="No Users found."
        onFilter={(e) => onFilter(e.filters)}
        style={{ fontFamily: "poppins" }}
        dataKey="id"
        loading={loading}
      >
        <Column sortable field="id" header="ID"></Column>
        <Column sortable field="username" header="User Name"></Column>
        <Column sortable field="email" header="Email"></Column>
        <Column field="roles" header="Role" body={roleBodyTemplate}></Column>
        <Column
          field="exportToSkeyechart"
          header="Skeyechart Access"
          body={skeyechartAccessTemplate}
        ></Column>
        <Column
          field="actions"
          header="Actions"
          body={actionBodyTemplate}
        ></Column>
      </DataTable>
      <Paginator
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        onPageChange={debouncedPageChange}
        disabled={loading}
      ></Paginator>

      <Dialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        header="Confirm Delete"
        footer={
          <>
            <Button
              label="No"
              icon="pi pi-times"
              onClick={() => setDeleteDialogVisible(false)}
              className="p-button-text"
            />
            <Button
              label="Yes"
              icon="pi pi-check"
              onClick={confirmDelete}
              className="p-button-danger"
            />
          </>
        }
      >
        <p>Are you sure you want to delete this user?</p>
      </Dialog>
      <Dialog
        visible={editDialogVisible}
        onHide={() => setEditDialogVisible(false)}
        header="Edit User"
        style={{ width: window.innerWidth > 768 ? "50vw" : "95vw" }}
        modal
        className="user-dialog"
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              flexDirection: window.innerWidth > 768 ? "row" : "column",
              gap: window.innerWidth > 768 ? "0.5rem" : "0.75rem",
            }}
          >
            <Button
              label="Cancel"
              icon="pi pi-times"
              style={{
                color: "#fff",
                backgroundColor: "#D97A07",
                border: "none",
              }}
              onClick={() => setEditDialogVisible(false)}
              className="p-button-text"
            />
            <Button
              label="Save"
              icon="pi pi-check"
              style={{
                color: "#fff",
                backgroundColor: "#0D2E4D",
                border: "none",
              }}
              onClick={confirmEdit}
            />
          </div>
        }
      >
        <div className="p-field p-fluid">
          <label htmlFor="password">New Password</label>
          <Password
            toggleMask
            id="password"
            type="password"
            value={editedPassword}
            onChange={(e) => setEditedPassword(e.target.value)}
            placeholder="Enter new password"
            feedback={false}
          />
        </div>
        <div className="p-field p-fluid">
          <label htmlFor="confirmPassword">Confirm New Password</label>
          <Password
            toggleMask
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            feedback={false}
          />
        </div>
        <div className="p-field p-fluid">
          <label htmlFor="role">Role</label>
          <Dropdown
            id="role"
            value={editedRole}
            options={roles}
            onChange={(e) => setEditedRole(e.value)}
            placeholder="Select a role"
          />
        </div>
      </Dialog>
    </div>
  );
};

export default UserTable;
